import type { DataViewStatus } from '@vtex/admin-ui'
import { csx, Skeleton, Stack, Text } from '@vtex/admin-ui'
import { ImageWithFallback } from '@/components/ImageWithFallback'
import {
  useNavigateToProductEditor,
  useProductEditorURL,
} from '@/components/products-listing/hooks/useEditorURLs'
import { Tooltip } from '@/components/Tooltip/Tooltip'
import { useAnalytics } from '@/contexts/analytics'
import { useProductOffersContext } from '@/contexts/productOffers'
import { useHintVisibilityByRef } from '@/hooks/useHintVisibilityByRef'
import { executeClickHandlers } from '@/lib/middleware'
import { handleAnchorClickSafely } from '@/lib/navigate'
import type { Product } from '@/types/product'

import { ProductQuickActions } from './ProductQuickAction/ProductQuickActions'
import { StatusIcon, useStatusIcon } from './status-icon'

import * as styles from './ProductCell.styles'
import * as productsListingStyles from './products-listing.styles'

export function ProductCell(props: ProductCellProps) {
  const { item, context } = props
  const status = useStatusIcon({
    active: item.isActive,
    visibleInPortal: item.isVisibleInPortal,
  })
  const analytics = useAnalytics()
  const { isLoadingOffers } = useProductOffersContext()
  const { isHintVisible, textRef } = useHintVisibilityByRef()
  const productLink = useProductEditorURL(item.id)
  const handleNavigateToProduct = useNavigateToProductEditor(item.id)

  const dispatchEventOnProductClick = () => {
    analytics.track('Clicked on the list', {
      Action: 'Edit product',
    })
  }

  if (context === 'loading') return <ProductCellSkeleton />

  return (
    <a
      href={productLink}
      onAuxClick={dispatchEventOnProductClick}
      onClick={executeClickHandlers(
        dispatchEventOnProductClick,
        handleAnchorClickSafely(handleNavigateToProduct)
      )}
      onContextMenu={dispatchEventOnProductClick}
      className={csx({ width: '100%', textDecoration: 'none' })}
    >
      <Stack
        direction="row"
        space="$space-3"
        className={csx({ overflow: 'hidden' })}
      >
        {isLoadingOffers ? <SkeletonDot /> : <StatusIcon {...status} />}
        <ImageWithFallback
          src={item.mainImageUrl}
          width={44}
          height={44}
          alt={item.name}
        />
        <Stack className={csx({ overflow: 'hidden' })}>
          <Tooltip text={item.name} enabled={isHintVisible}>
            <Text
              ref={textRef}
              role="heading"
              variant="action1"
              className={csx(productsListingStyles.productNameEllipsis)}
            >
              {item.name}
            </Text>
          </Tooltip>
          <Text tone="secondary">{item.id}</Text>
        </Stack>
        <ProductQuickActions product={item} />
      </Stack>
    </a>
  )
}

export const SkeletonDot = () => (
  <Skeleton
    shape="circle"
    className={csx(styles.skeletonDot)}
    data-testid="status-icon-skeleton"
  />
)

function ProductCellSkeleton() {
  return (
    <Stack direction="row" space="$space-3" data-testid="product-cell-skeleton">
      <Stack direction="row" className={csx(styles.skeletonRow)}>
        <SkeletonDot />
      </Stack>
      <Skeleton
        className={csx(
          productsListingStyles.productImageContainer({ type: 'fallback' })
        )}
      />
      <Stack>
        <Skeleton className={csx({ height: '0.75rem', width: '6rem' })} />
        <Skeleton className={csx({ height: '0.75rem', width: '2.5rem' })} />
      </Stack>
    </Stack>
  )
}

interface ProductCellProps {
  item: Product
  context: DataViewStatus
}
